import React, { Component } from "react";
import { Typography, Card, CardContent, Button } from "@material-ui/core";
import moment from "moment";
import { KeyboardDatePicker } from "@material-ui/pickers";
import { languages } from "../../constants/languages";

class DormantUpgradeUsersExportContainer extends Component {
  state = {
    loading: false,
    csvData: null,
    exportedCount: 0,
    logs: [],
    error: null,
    periodStart: moment("2025-03-01"),
    periodEnd: moment("2025-06-30"),
  };

  addLog = (message) => {
    const timestamp = new Date().toLocaleTimeString();
    const logEntry = `[${timestamp}] ${message}`;
    this.setState((prevState) => ({
      logs: [...prevState.logs, logEntry],
    }));
  };

  toUnixFromTimestamp = (value) => {
    if (!value) {
      return null;
    }
    if (value.toDate) {
      return moment(value.toDate()).unix();
    }
    if (value.seconds !== undefined) {
      return value.seconds;
    }
    if (value instanceof Date) {
      return moment(value).unix();
    }
    return null;
  };

  chunkArray = (array, size) => {
    let chunks = [];
    for (let i = 0; i < array.length; i += size) {
      chunks.push(array.slice(i, i + size));
    }
    return chunks;
  };

  escapeCsv = (value) => {
    const stringValue =
      value === null || value === undefined ? "" : String(value);
    const escaped = stringValue.replace(/"/g, '""');
    if (
      escaped.includes(",") ||
      escaped.includes('"') ||
      escaped.includes("\n")
    ) {
      return `"${escaped}"`;
    }
    return escaped;
  };

  handleStartChange = (date) => {
    this.setState({ periodStart: date });
  };

  handleEndChange = (date) => {
    this.setState({ periodEnd: date });
  };

  processChunksInBatches = async ({
    chunks,
    batchSize,
    logLabel,
    fetchChunk,
    onChunkResult,
  }) => {
    const totalChunks = chunks.length;
    if (!totalChunks) {
      this.addLog(`${logLabel}: no chunks to process`);
      return;
    }

    this.addLog(
      `${logLabel}: processing ${totalChunks} chunks (batch size ${batchSize})`
    );

    let processed = 0;
    for (let i = 0; i < totalChunks; i += batchSize) {
      const batchChunks = chunks.slice(i, i + batchSize);
      const snapshots = await Promise.all(
        batchChunks.map((chunk) => fetchChunk(chunk))
      );

      snapshots.forEach((snapshot) => {
        onChunkResult(snapshot);
      });

      processed += batchChunks.length;
      this.addLog(`${logLabel}: ${processed}/${totalChunks} chunks done`);
    }
  };

  runExport = async () => {
    const periodStartMoment = this.state.periodStart;
    const periodEndMoment = this.state.periodEnd;

    if (!periodStartMoment || !periodEndMoment) {
      this.setState({ error: "Please select both start and end date." });
      return;
    }

    const periodStart = periodStartMoment.clone().startOf("day").toDate();
    const periodEnd = periodEndMoment.clone().endOf("day").toDate();
    const periodStartUnix = periodStartMoment.clone().startOf("day").unix();
    const periodEndUnix = periodEndMoment.clone().endOf("day").unix();

    if (periodStartUnix > periodEndUnix) {
      this.setState({ error: "Start date cannot be after end date." });
      return;
    }

    this.setState({
      loading: true,
      csvData: null,
      exportedCount: 0,
      logs: [],
      error: null,
    });

    try {
      this.addLog(
        `Selected period: ${moment(periodStart).format("YYYY-MM-DD")} to ${moment(
          periodEnd
        ).format("YYYY-MM-DD")}`
      );
      this.addLog("Loading closed tournament statuses...");
      const statusSnapshot = await window.firebase
        .firestore()
        .collection("tournamentStatus")
        .where("closed", "==", true)
        .get();

      let statusByTournamentId = {};
      statusSnapshot.forEach((doc) => {
        statusByTournamentId[doc.id] = doc.data() || {};
      });

      const statusTournamentIds = Object.keys(statusByTournamentId);
      this.addLog(
        `Found ${statusTournamentIds.length} closed tournament status docs`
      );

      if (!statusTournamentIds.length) {
        this.setState({
          loading: false,
          csvData: null,
          exportedCount: 0,
        });
        return;
      }

      this.addLog("Preparing tournament fetch chunks...");
      const tournamentChunks = this.chunkArray(statusTournamentIds, 10);
      let tournamentsById = {};

      await this.processChunksInBatches({
        chunks: tournamentChunks,
        batchSize: 5,
        logLabel: "Tournaments",
        fetchChunk: (chunk) =>
          window.firebase
            .firestore()
            .collection("tournaments")
            .where(window.firebase.firestore.FieldPath.documentId(), "in", chunk)
            .get(),
        onChunkResult: (snapshot) => {
          snapshot.forEach((doc) => {
            tournamentsById[doc.id] = { id: doc.id, ...doc.data() };
          });
        },
      });

      this.addLog(`Loaded ${Object.keys(tournamentsById).length} tournaments`);

      const upgradedClosedTournamentIds = statusTournamentIds.filter((id) => {
        const tournament = tournamentsById[id] || {};
        return (
          [1, 2, 3].indexOf(tournament.status) !== -1 &&
          tournament.date >= periodStartUnix &&
          tournament.date <= periodEndUnix
        );
      });
      this.addLog(
        `${upgradedClosedTournamentIds.length} tournaments are closed, upgraded (status 1/2/3) and in selected tournament date period`
      );

      if (!upgradedClosedTournamentIds.length) {
        this.setState({
          loading: false,
          csvData: null,
          exportedCount: 0,
        });
        return;
      }

      this.addLog("Loading paid upgrades in period for these tournaments...");
      const tournamentUpgradeChunks = this.chunkArray(
        upgradedClosedTournamentIds,
        10
      );
      let latestPeriodUpgradeByTournamentId = {};
      await this.processChunksInBatches({
        chunks: tournamentUpgradeChunks,
        batchSize: 5,
        logLabel: "Period upgrades",
        fetchChunk: (chunk) =>
          window.firebase
            .firestore()
            .collection("upgrades")
            .where("payed", "==", true)
            .where("tournamentId", "in", chunk)
            .where("timestamp", ">=", periodStart)
            .where("timestamp", "<=", periodEnd)
            .get(),
        onChunkResult: (periodUpgradeSnapshot) => {
          periodUpgradeSnapshot.forEach((doc) => {
            const upgrade = doc.data();
            const tournamentId = upgrade.tournamentId;
            const upgradeTimestamp = this.toUnixFromTimestamp(upgrade.timestamp);
            if (!tournamentId || !upgrade.uid || !upgradeTimestamp) {
              return;
            }

            const previous = latestPeriodUpgradeByTournamentId[tournamentId];
            if (!previous || upgradeTimestamp > previous.timestamp) {
              latestPeriodUpgradeByTournamentId[tournamentId] = {
                id: doc.id,
                ...upgrade,
                timestamp: upgradeTimestamp,
              };
            }
          });
        },
      });

      const candidateUpgrades = Object.keys(latestPeriodUpgradeByTournamentId).map(
        (tournamentId) => latestPeriodUpgradeByTournamentId[tournamentId]
      );
      this.addLog(
        `Found ${candidateUpgrades.length} tournaments with paid upgrade in period`
      );

      const candidateUids = [
        ...new Set(candidateUpgrades.map((upgrade) => upgrade.uid).filter(Boolean)),
      ];
      const candidateUidChunks = this.chunkArray(candidateUids, 10);

      this.addLog(
        `Checking if these users purchased again after the selected end date (${moment(
          periodEnd
        ).format("ll")})...`
      );
      let disqualifiedUids = {};
      await this.processChunksInBatches({
        chunks: candidateUidChunks,
        batchSize: 5,
        logLabel: "Repeat purchase check",
        fetchChunk: (chunk) =>
          window.firebase
            .firestore()
            .collection("upgrades")
            .where("payed", "==", true)
            .where("uid", "in", chunk)
            .where("timestamp", ">", periodEnd)
            .get(),
        onChunkResult: (newerSnapshot) => {
          newerSnapshot.forEach((doc) => {
            const upgrade = doc.data();
            if (upgrade.uid) {
              disqualifiedUids[upgrade.uid] = true;
            }
          });
        },
      });

      const qualifiedUpgrades = candidateUpgrades.filter(
        (upgrade) => !disqualifiedUids[upgrade.uid]
      );

      this.addLog(
        `${qualifiedUpgrades.length} records remain after excluding purchases after selected end date`
      );

      const userIds = [
        ...new Set(
          qualifiedUpgrades
            .map((upgrade) => {
              const tournament = tournamentsById[upgrade.tournamentId] || {};
              return tournament.uid;
            })
            .filter(Boolean)
        ),
      ];
      const userChunks = this.chunkArray(userIds, 10);
      let usersById = {};

      await this.processChunksInBatches({
        chunks: userChunks,
        batchSize: 5,
        logLabel: "Users",
        fetchChunk: (chunk) =>
          window.firebase
            .firestore()
            .collection("users")
            .where(window.firebase.firestore.FieldPath.documentId(), "in", chunk)
            .get(),
        onChunkResult: (snapshot) => {
          snapshot.forEach((doc) => {
            usersById[doc.id] = { id: doc.id, ...doc.data() };
          });
        },
      });

      this.addLog(`Loaded ${Object.keys(usersById).length} users`);

      const headers = [
        "email",
        "name",
        "registeredLang",
        "date",
        "tournamentName",
        "numTeams",
        "numMatches",
        "sportId",
        "place",
        "upgradeUid",
        "upgradeTimestamp",
      ];

      const rows = qualifiedUpgrades
        .sort((a, b) => b.timestamp - a.timestamp)
        .map((upgrade) => {
          const tournament = tournamentsById[upgrade.tournamentId] || {};
          const ownerUid = tournament.uid;
          const user = usersById[ownerUid] || {};

          return [
            this.escapeCsv(user.email || ""),
            this.escapeCsv(user.name || user.firstName || ""),
            this.escapeCsv(user.registeredLang || ""),
            this.escapeCsv(
              tournament.date
                ? moment.unix(tournament.date).format("YYYY-MM-DD")
                : ""
            ),
            this.escapeCsv(tournament.name || ""),
            this.escapeCsv(tournament.numTeams || ""),
            this.escapeCsv(tournament.numMatches || ""),
            this.escapeCsv(tournament.sportId || ""),
            this.escapeCsv(tournament.place || ""),
            this.escapeCsv(upgrade.uid || ""),
            this.escapeCsv(moment.unix(upgrade.timestamp).format("YYYY-MM-DD")),
          ].join(",");
        });

      const csvData = [headers.join(","), ...rows].join("\n");

      this.setState({
        loading: false,
        csvData,
        exportedCount: qualifiedUpgrades.length,
      });
      this.addLog("Report generated and ready for download");
    } catch (error) {
      console.error("Error generating dormant upgrade export:", error);
      this.setState({
        loading: false,
        error: "Failed to generate export report",
      });
      this.addLog(`Error: ${error.message}`);
    }
  };

  downloadCsv = () => {
    if (!this.state.csvData) {
      return;
    }
    const blob = new Blob([this.state.csvData], {
      type: "text/csv;charset=utf-8;",
    });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    const periodString = `${this.state.periodStart
      .clone()
      .format("YYYY-MM-DD")}_${this.state.periodEnd
      .clone()
      .format("YYYY-MM-DD")}`;
    link.setAttribute(
      "download",
      `dormant_upgrade_users_${periodString}.csv`
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  render() {
    return (
      <div style={{ padding: "20px" }}>
        <Typography variant="h4" gutterBottom>
          Dormant Upgrade Users Export
        </Typography>
        <Typography variant="body1" style={{ marginBottom: 16 }}>
          Select the last-upgrade tournament date period. The report exports
          closed tournaments with upgraded status (1/2/3) in that period, then
          excludes users who purchased a newer paid upgrade after the selected
          end date.
        </Typography>
        <div
          style={{
            display: "flex",
            gap: 10,
            alignItems: "baseline",
            marginBottom: 16,
          }}
        >
          <KeyboardDatePicker
            margin="normal"
            name="periodStart"
            label="Period start"
            format={languages.createTournament.dateFormat}
            cancelLabel={languages.createTournament.cancel}
            autoOk={true}
            value={this.state.periodStart}
            onChange={this.handleStartChange}
            placeholder={languages.createTournament.datePlaceholder}
            invalidLabel={languages.createTournament.invalidDateLabel}
          />
          <KeyboardDatePicker
            margin="normal"
            name="periodEnd"
            label="Period end"
            format={languages.createTournament.dateFormat}
            cancelLabel={languages.createTournament.cancel}
            autoOk={true}
            value={this.state.periodEnd}
            onChange={this.handleEndChange}
            placeholder={languages.createTournament.datePlaceholder}
            invalidLabel={languages.createTournament.invalidDateLabel}
          />
        </div>
        <div style={{ display: "flex", gap: 10, marginBottom: 20 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={this.runExport}
            disabled={
              this.state.loading ||
              !this.state.periodStart ||
              !this.state.periodEnd
            }
          >
            {this.state.loading ? "Generating..." : "Generate report"}
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={this.downloadCsv}
            disabled={!this.state.csvData || this.state.loading}
          >
            Download CSV ({this.state.exportedCount})
          </Button>
        </div>
        {this.state.error && (
          <Typography color="error" style={{ marginBottom: 16 }}>
            {this.state.error}
          </Typography>
        )}
        <Card>
          <CardContent>
            <Typography variant="h6" color="primary" gutterBottom>
              Process Logs
            </Typography>
            <div
              style={{
                maxHeight: "300px",
                overflowY: "auto",
                backgroundColor: "#f5f5f5",
                padding: "10px",
                fontFamily: "monospace",
                fontSize: "12px",
              }}
            >
              {this.state.logs.length === 0 ? (
                <div>No logs yet</div>
              ) : (
                this.state.logs.map((log, index) => (
                  <div key={index} style={{ marginBottom: "2px" }}>
                    {log}
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }
}

export default DormantUpgradeUsersExportContainer;
